/**
 * Phase 14.5 Step 3 — ModulationThresholds sub-form (controlled leaf).
 *
 * Registry-driven from `modulation_thresholds.fields`. Each field
 * carries its own default in the registry payload — read it on first
 * mount so operators see the platform defaults exactly as the
 * Pydantic `ModulationThresholds()` constructor would seed them.
 *
 * Fields:
 *   terminal_window_fraction        (0..1, exclusive)
 *   latency_terminal_fraction       (0..1)
 *   gyro_drift_threshold_deg        (>0)
 *   reacquire_window_s              (>0)
 *   reacquire_bernoulli_p           (0..1)
 *   range_bias_uses_payload_radius  bool
 *   fixed_range_bias_threshold_m    (≥0)
 *
 * Held to the Step-2 invariant: no internal `useForm`.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

function _setField(value, field, raw, kind, onChange) {
    let parsed;
    if (kind === "bool") parsed = !!raw;
    else if (kind === "int") parsed = parseInt(raw || "0", 10);
    else parsed = parseFloat(raw || "0");
    onChange?.({
        ...(value || {}),
        [field]: kind === "bool" ? parsed : (Number.isFinite(parsed) ? parsed : 0),
    });
}

export default function ModulationThresholdsSubForm({ value, onChange, registry }) {
    const fields = Array.isArray(registry?.fields) ? registry.fields : [];

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="modulation-thresholds-subform">
            {fields.map((f) => {
                const isBool = f.unit === "bool";
                const lo = f.physical_bounds?.lo;
                const hi = f.physical_bounds?.hi;
                const note = f.physical_bounds?.note;
                const current = value?.[f.id] !== undefined ? value[f.id] : f.default;
                if (isBool) {
                    return (
                        <div
                            key={f.id}
                            className="flex items-start gap-2 rounded-md border border-ux-border-1 bg-ux-surface-2/30 p-2"
                            data-testid={`modulation-field-${f.id}`}
                        >
                            <Switch
                                checked={!!current}
                                onCheckedChange={(v) => _setField(value, f.id, v, "bool", onChange)}
                                data-testid={`modulation-${f.id}-toggle`}
                                aria-label={f.id}
                            />
                            <div>
                                <Label className="block text-xs font-medium text-ux-text-1">
                                    {f.id}
                                </Label>
                                {note && <p className="text-[11px] text-ux-text-3">{note}</p>}
                            </div>
                        </div>
                    );
                }
                return (
                    <div key={f.id} data-testid={`modulation-field-${f.id}`}>
                        <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            {f.id}{" "}
                            {f.unit && <span className="text-ux-text-3/60">({f.unit})</span>}
                        </Label>
                        <Input
                            type="number"
                            step="any"
                            min={lo}
                            max={hi}
                            value={current ?? 0}
                            onChange={(e) => _setField(value, f.id, e.target.value, "scalar", onChange)}
                            data-testid={`modulation-${f.id}`}
                        />
                        {note && <p className="mt-1 text-[11px] text-ux-text-3">{note}</p>}
                    </div>
                );
            })}
        </div>
    );
}
